import {
  addToast,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/react';
import React, { useEffect, useState } from 'react';
import CustomButton from '../../CustomButton';
import BecomeProviderButton from './BecomeProviderButton';
import CheckoutModal, {
  mapApiPlanToCheckout,
  type CheckoutPlan,
} from './CheckoutModal';
import BillingCycleToggle from './BillingCycleToggle';
import {
  Check,
  X,
  Zap,
  Rocket,
  Crown,
  UserCog,
  ArrowRight,
  BarChart3,
  CalendarCheck,
  Briefcase,
  Users,
  Star,
  Plus,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { all_routes } from '../../../../core/data/routes/all_routes';
import { useAuth } from '../../../../context/AuthContext';
import { becomeAProvider } from '../../../../service/userService';
import {
  useFetchAllMembershipPlans,
  useSubmitMembershipMutation,
} from '../../../../hook/useQueryData';
import { handleProviderLogin } from '../../auth/apiAuth';
import { cn } from '../../../../utils/type';
import {
  formatPlanFeatureLabel,
  getAnalyticsValue,
  getFeaturedListingsLimit,
  isBookingCalendarEnabled,
  isPopularPlan,
} from '../../../../utils/membershipPlanUtils';

const SUCCESS_FLAG_KEY = 'provider_trial_success';

type FeatureTier = 'none' | 'basic' | 'advanced' | 'premium';

interface PlanFeature {
  key: string;
  label: string;
  icon: LucideIcon;
  tier: FeatureTier;
  included: boolean;
}

const PLAN_TAGLINES: Record<string, string> = {
  'free explorer': 'Get started, no card needed',
  'growth builder': 'Free for 6 months · one-time trial',
  'business pro': 'For growing service teams',
};

const PLAN_ICONS: Record<number, LucideIcon> = {
  0: Zap,
  1: Rocket,
  2: Crown,
};

function getPlanIcon(tier: number): LucideIcon {
  return PLAN_ICONS[tier] ?? Crown;
}

function getPlanTagline(planName: string): string {
  const key = planName.toLowerCase();
  return PLAN_TAGLINES[key] ?? 'Scale your service business';
}

function formatPrice(value: number | 'free' | 'custom') {
  if (value === 'free') return 'Free';
  if (value === 'custom') return 'Custom';
  return `CAD ${Number(value).toFixed(2)}`;
}

function getFeatureTierForPlan(planTier: number): FeatureTier {
  if (planTier >= 2) return 'premium';
  if (planTier >= 1) return 'advanced';
  return 'basic';
}

function hasAddon(plan: any, pattern: RegExp): boolean {
  return (plan.addons ?? []).some(
    (a: any) => pattern.test(a.code || '') || pattern.test(a.name || '')
  );
}

function getAnalyticsLabel(plan: any): string {
  const raw = String(getAnalyticsValue(plan) ?? '').toLowerCase();
  if (raw.includes('advance')) return 'Advanced growth analytics';
  if (raw.includes('basic')) return 'Basic performance analytics';
  const formatted = formatPlanFeatureLabel(getAnalyticsValue(plan));
  return formatted === '—' ? 'Service analytics' : `${formatted} analytics`;
}

function getTeamLabel(count: number): string {
  if (count <= 0) return 'No team members';
  if (count === 1) return 'Add 1 team member';
  return `Add up to ${count} team members`;
}

function getFeaturedLabel(count: number | string): {
  label: string;
  included: boolean;
} {
  const n =
    typeof count === 'number' ? count : Number(String(count).split('/')[0]);
  if (!n || Number.isNaN(n) || n === 0) {
    return { label: 'No featured listings', included: false };
  }
  if (n === 1) {
    return { label: '1 featured listing / month', included: true };
  }
  return { label: `${n} featured listings / month`, included: true };
}

/** Build the 7 feature rows from membership API payload. */
function buildPlanFeatures(plan: any): PlanFeature[] {
  const planTier = getFeatureTierForPlan(plan.tier ?? 0);
  const bookingIncluded = isBookingCalendarEnabled(plan);
  const services = plan.limits?.services ?? 0;
  const teamMembers = plan.limits?.teamMembers ?? 0;
  const featured = getFeaturedLabel(getFeaturedListingsLimit(plan));
  const hasExtraTeam = hasAddon(plan, /extra_team|team member/i);
  const hasAdditionalServices = hasAddon(
    plan,
    /additional_service|additional service/i
  );

  return [
    {
      key: 'analytics',
      label: getAnalyticsLabel(plan),
      icon: BarChart3,
      tier: planTier,
      included: true,
    },
    {
      key: 'booking',
      label: 'Built-in booking calendar',
      icon: CalendarCheck,
      tier: bookingIncluded ? 'basic' : 'none',
      included: bookingIncluded,
    },
    {
      key: 'services',
      label: `List up to ${services} services`,
      icon: Briefcase,
      tier: services > 0 ? planTier : 'none',
      included: services > 0,
    },
    {
      key: 'team',
      label: getTeamLabel(teamMembers),
      icon: Users,
      tier: teamMembers > 0 ? planTier : 'none',
      included: teamMembers > 0,
    },
    {
      key: 'featured',
      label: featured.label,
      icon: Star,
      tier: featured.included ? planTier : 'none',
      included: featured.included,
    },
    {
      key: 'extraTeam',
      label: 'Extra team members add-on',
      icon: UserPlus,
      tier: hasExtraTeam ? planTier : 'none',
      included: hasExtraTeam,
    },
    {
      key: 'additional',
      label: 'Additional services add-on',
      icon: Plus,
      tier: hasAdditionalServices ? planTier : 'none',
      included: hasAdditionalServices,
    },
  ];
}

interface BecomeProviderModalProps {
  externalIsOpen?: boolean;
  externalOnOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

interface PlanCardProps {
  plan: any;
  isAnnual: boolean;
  loadingPlanId: string | null;
  onSelect: (planId: string, isAnnual: boolean) => void;
}

function PlanCard({ plan, isAnnual, loadingPlanId, onSelect }: PlanCardProps) {
  const Icon = getPlanIcon(plan.tier);
  const isFreePlan = plan.tier === 0;
  const isCustomPlan = plan.name.toLowerCase().includes('enterprise');
  const isPopular = isPopularPlan(plan);
  const isLoading = loadingPlanId === plan.tier;
  const features = buildPlanFeatures(plan);

  const price = isFreePlan
    ? 'free'
    : isCustomPlan
      ? 'custom'
      : isAnnual
        ? plan.pricing.yearly.price
        : plan.pricing.monthly.price;
  const period = isAnnual ? '/year' : '/month';
  const isFree = price === 'free';

  const getTrialLabel = () => {
    if (plan.freeTrialEnabled && plan.freeTrialDays > 0) {
      const months = Math.round(plan.freeTrialDays / 30);
      return `${months} Month${months > 1 ? 's' : ''} Free Trial`;
    }
    return null;
  };

  const trialLabel = getTrialLabel();

  const getButtonLabel = () => {
    if (isLoading) return 'Processing...';
    if (isCustomPlan) return 'Contact Sales';
    if (isFreePlan) return 'Start Free Plan';
    if (trialLabel) return `Claim ${trialLabel}`;
    return `Continue with ${plan.name}`;
  };

  return (
    <div
      className={cn(
        'relative rounded-2xl p-4 sm:p-5 flex flex-col transition-all duration-300 hover:-translate-y-1',
        isPopular
          ? 'text-primary-foreground'
          : 'bg-background border border-border hover:border-primary/40 hover:shadow-lg'
      )}
      style={
        isPopular
          ? {
              background: 'var(--gradient-popular)',
              boxShadow: 'var(--shadow-popular)',
            }
          : undefined
      }
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div
            className="flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg"
            style={{ background: 'var(--accent-orange)' }}
          >
            Most Popular
          </div>
        </div>
      )}

      {trialLabel && !isPopular && (
        <div className="absolute -top-2 right-3">
          <div
            className="flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg"
            style={{ background: 'var(--gradient-primary)' }}
          >
            {trialLabel}
          </div>
        </div>
      )}

      <div
        className={cn(
          'h-9 w-9 rounded-lg flex items-center justify-center mb-2.5',
          isPopular ? 'bg-white/15 backdrop-blur-sm' : 'bg-primary/10'
        )}
      >
        <Icon
          className={cn('h-4 w-4', isPopular ? 'text-white' : 'text-primary')}
        />
      </div>

      <h3
        className={cn(
          'text-base font-bold',
          isPopular ? 'text-white' : 'text-foreground'
        )}
      >
        {plan.name}
      </h3>
      <p
        className={cn(
          'text-[11px] mt-0.5 mb-3',
          isPopular ? 'text-white/80' : 'text-muted-foreground'
        )}
      >
        {getPlanTagline(plan.name)}
      </p>

      <div className="mb-3">
        {isFree ? (
          <div
            className={cn(
              'text-2xl font-bold',
              isPopular ? 'text-white' : 'text-foreground'
            )}
          >
            Free
          </div>
        ) : isCustomPlan ? (
          <div
            className={cn(
              'text-2xl font-bold',
              isPopular ? 'text-white' : 'text-foreground'
            )}
          >
            Custom
          </div>
        ) : (
          <div className="flex items-baseline gap-1">
            <span
              className={cn(
                'text-2xl font-bold',
                isPopular ? 'text-white' : 'text-foreground'
              )}
            >
              {formatPrice(price)}
            </span>
            <span
              className={cn(
                'text-xs',
                isPopular ? 'text-white/70' : 'text-muted-foreground'
              )}
            >
              {period}
            </span>
          </div>
        )}
      </div>

      <ul className="space-y-2 mb-4 flex-1">
        {features.map((feature) => (
          <FeatureRow
            key={feature.key}
            feature={feature}
            highlight={isPopular}
          />
        ))}
      </ul>

      {isCustomPlan ? (
        <a
          href="/contact"
          className={cn(
            'w-full font-semibold rounded-lg h-10 text-sm flex items-center justify-center',
            isPopular
              ? 'bg-white text-primary hover:bg-white/90'
              : 'bg-foreground text-background hover:bg-foreground/90'
          )}
        >
          Contact Sales
        </a>
      ) : (
        <button
          type="button"
          onClick={() => onSelect(plan.tier, isAnnual)}
          disabled={isLoading}
          className={cn(
            'w-full font-semibold rounded-lg h-10 text-sm transition-colors disabled:opacity-60',
            isPopular
              ? 'bg-white text-primary hover:bg-white/90'
              : 'bg-foreground text-background hover:bg-foreground/90'
          )}
        >
          {getButtonLabel()}
        </button>
      )}
    </div>
  );
}

function FeatureRow({
  feature,
  highlight,
}: {
  feature: PlanFeature;
  highlight: boolean;
}) {
  const FeatureIcon = feature.icon;
  const isIncluded = feature.included;

  const tierStyles: Record<
    FeatureTier,
    { bg: string; fg: string; ring?: string }
  > = highlight
    ? {
        none: {
          bg: 'bg-white/10 opacity-60',
          fg: 'text-white/60',
        },
        basic: {
          bg: 'bg-white/20',
          fg: 'text-white',
        },
        advanced: {
          bg: 'bg-white/25 backdrop-blur-md',
          fg: 'text-white',
          ring: 'ring-1 ring-white/40 shadow-sm',
        },
        premium: {
          bg: 'bg-amber-400 text-amber-950 shadow-md shadow-amber-500/20',
          fg: 'text-amber-950',
          ring: 'ring-1 ring-amber-300',
        },
      }
    : {
        none: {
          bg: 'bg-muted/80',
          fg: 'text-muted-foreground/60',
        },
        basic: {
          bg: 'bg-emerald-500/10 dark:bg-emerald-500/20',
          fg: 'text-emerald-600 dark:text-emerald-400',
        },
        advanced: {
          bg: 'bg-primary/10 dark:bg-primary/20',
          fg: 'text-primary',
          ring: 'ring-1 ring-primary/20',
        },
        premium: {
          bg: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20',
          fg: 'text-amber-600 dark:text-amber-400',
          ring: 'ring-1 ring-amber-500/30 shadow-sm',
        },
      };

  const s = tierStyles[feature.tier];

  return (
    <li className="flex items-start gap-2 text-xs sm:text-[13px]">
      <span
        className={cn(
          'shrink-0 mt-0.5 h-5 w-5 rounded-md flex items-center justify-center relative transition-all duration-200',
          s.bg,
          s.ring,
          feature.tier !== 'none' && 'shadow-sm'
        )}
      >
        {!isIncluded ? (
          <X className={cn('h-3 w-3 stroke-[2.5]', s.fg)} />
        ) : feature.tier === 'premium' ? (
          <Crown className={cn('h-3 w-3 stroke-[2.5]', s.fg)} />
        ) : feature.tier === 'advanced' ? (
          <Zap className={cn('h-3 w-3 stroke-[2.5]', s.fg)} />
        ) : (
          <Check className={cn('h-3 w-3 stroke-[3]', s.fg)} />
        )}
      </span>
      {/* Feature Icon */}
      <FeatureIcon
        className={cn(
          'shrink-0 h-3.5 w-3.5 transition-colors duration-200',
          highlight
            ? isIncluded
              ? 'text-white/80'
              : 'text-white/40'
            : isIncluded
              ? 'text-muted-foreground group-hover:text-foreground'
              : 'text-muted-foreground/40'
        )}
      />

      {/* Feature Label */}
      <span
        className={cn(
          'transition-colors duration-200 select-none',
          !isIncluded && 'line-through opacity-60',
          highlight
            ? isIncluded
              ? 'text-white font-medium'
              : 'text-white/50'
            : isIncluded
              ? 'text-foreground font-medium'
              : 'text-muted-foreground'
        )}
      >
        {feature.label}
      </span>
    </li>
  );
}

const BecomeProviderModal: React.FC<BecomeProviderModalProps> = ({
  externalIsOpen,
  externalOnOpenChange,
  hideTrigger = false,
}) => {
  const { isAuthenticated, userId, user } = useAuth();
  const internalDisclosure = useDisclosure();

  const isOpen =
    externalIsOpen !== undefined ? externalIsOpen : internalDisclosure.isOpen;
  const onOpen =
    externalIsOpen !== undefined
      ? () => externalOnOpenChange?.(true)
      : internalDisclosure.onOpen;
  const onOpenChange = externalOnOpenChange || internalDisclosure.onOpenChange;
  const closeOfferModal =
    externalIsOpen !== undefined
      ? () => externalOnOpenChange?.(false)
      : internalDisclosure.onClose;
  const { onOpen: openSuccessModal } = useDisclosure();

  const [isLoading, setIsLoading] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const { data } = useFetchAllMembershipPlans();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const { mutate } = useSubmitMembershipMutation();
  const [showFreeConfirmModal, setShowFreeConfirmModal] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<CheckoutPlan | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    const success = sessionStorage.getItem(SUCCESS_FLAG_KEY);
    if (success === 'true') {
      sessionStorage.removeItem(SUCCESS_FLAG_KEY);
      openSuccessModal();
    }
  }, [openSuccessModal]);

  const handleBecomeProvider = async () => {
    try {
      setIsLoading(true);
      if (!userId) throw new Error('User ID is missing');
      if (!isAuthenticated) throw new Error('User is not authenticated');

      const res = await becomeAProvider(userId);
      console.log('Become a provider response: ', res);
      // Re-login against the provider portal so Cognito SSO rebuilds the session
      // with the freshly-assigned `provider` role and the backend lands the user
      // directly on the provider dashboard (not the customer site).
      handleProviderLogin();

      if (res.success) {
        sessionStorage.setItem(SUCCESS_FLAG_KEY, 'true');
        setIsLoading(false);
        closeOfferModal();
      }
    } catch (err: any) {
      setIsLoading(false);
      console.error('Become a provider failed', err);
      addToast({
        title: 'Error becoming a provider',
        description: err
          ? err.message
          : 'Something went wrong. Please try again later.',
        radius: 'md',
        color: 'danger',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = async (planId: string, _annual: boolean) => {
    try {
      if (!userId) throw new Error('User ID is missing');
      if (!isAuthenticated) throw new Error('User is not authenticated');

      const selected = data?.plans?.find(
        (p: any) => String(p.tier) === String(planId)
      );
      if (!selected) throw new Error('Plan not found');

      if (selected.tier === 0) {
        setShowFreeConfirmModal(true);
        return;
      }

      setCheckoutPlan(mapApiPlanToCheckout(selected));
      setCheckoutOpen(true);
    } catch (err: any) {
      addToast({
        title: 'Subscription Error',
        description: err.message || 'Something went wrong',
        color: 'danger',
        radius: 'md',
      });
    }
  };

  const handleConfirmCheckout = () => {
    if (!checkoutPlan || loadingPlanId !== null) return;
    setLoadingPlanId(checkoutPlan.tier);
    localStorage.setItem('membershipPlanState', 'true');
    localStorage.setItem('membershipPlanName', checkoutPlan.name);
    mutate(
      {
        planId: checkoutPlan.tier,
        isAnnual,
        customerEmail: user?.email,
      },
      {
        onSettled: () => setLoadingPlanId(null),
      }
    );
  };

  const sortedPlans = [...(data?.plans ?? [])].sort((a, b) => a.tier - b.tier);
  const gridCols =
    sortedPlans.length >= 4
      ? 'sm:grid-cols-2 lg:grid-cols-4'
      : sortedPlans.length === 3
        ? 'sm:grid-cols-2 lg:grid-cols-3'
        : 'sm:grid-cols-2';

  return (
    <>
      {!hideTrigger && <BecomeProviderButton onClick={onOpen} />}

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="5xl"
        scrollBehavior="inside"
        hideCloseButton={false}
        classNames={{
          wrapper: 'items-start sm:items-center justify-center p-2 sm:p-4',
          base: cn(
            'max-w-[1100px] w-[calc(100vw-1.5rem)] !max-h-[95vh] my-0 min-h-0',
            'flex flex-col bg-transparent shadow-none overflow-hidden'
          ),
          body: cn(
            'p-0 min-h-0 flex-1 overflow-y-auto',
            'overscroll-y-contain [-webkit-overflow-scrolling:touch]'
          ),
          header: 'hidden',
          footer: 'hidden',
          closeButton:
            'top-3 right-3 z-20 bg-background/80 backdrop-blur-sm rounded-full',
        }}
      >
        <ModalContent className="p-0 gap-0 border-0 bg-transparent shadow-none min-h-0 flex flex-col flex-1 overflow-hidden">
          {() => (
            <div className="relative flex min-h-0 flex-1 flex-col rounded-2xl bg-card shadow-2xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-60">
                <div
                  className="absolute -top-32 -left-32 h-72 w-72 rounded-full blur-3xl opacity-40"
                  style={{ background: 'var(--gradient-primary)' }}
                />
                <div
                  className="absolute -bottom-32 -right-32 h-72 w-72 rounded-full blur-3xl opacity-30"
                  style={{ background: 'var(--gradient-popular)' }}
                />
              </div>

              <ModalBody className="relative min-h-0 flex-1 px-4 pt-5 pb-6 gap-0 sm:px-6 lg:px-8">
                <div className="flex items-start gap-3 mb-3 sm:mb-4 pr-8">
                  <div
                    className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                    style={{ background: 'var(--gradient-primary)' }}
                  >
                    <UserCog className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight text-foreground">
                      Become a Premium Service Provider
                    </h2>
                    <p className="mt-0.5 text-xs sm:text-sm text-muted-foreground">
                      Ready to scale your business?{' '}
                      <span className="font-semibold text-foreground">
                        Get started with 6 months FREE
                      </span>
                      , no commitment.
                    </p>
                  </div>
                </div>

                <div className="flex justify-center mb-4 sm:mb-5">
                  <BillingCycleToggle
                    isAnnual={isAnnual}
                    onChange={setIsAnnual}
                  />
                </div>

                <div
                  className={cn('grid grid-cols-1 gap-3 sm:gap-4', gridCols)}
                >
                  {sortedPlans.map((plan: any) => (
                    <PlanCard
                      key={plan.name}
                      plan={plan}
                      isAnnual={isAnnual}
                      loadingPlanId={loadingPlanId}
                      onSelect={handleClick}
                    />
                  ))}
                </div>

                <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-[11px] sm:text-xs text-muted-foreground">
                  <span>
                    All plans include 24/7 support · Cancel anytime · Secure
                    checkout
                  </span>
                  <span className="hidden sm:inline opacity-40">·</span>
                  <a
                    href={all_routes.comparePlans}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-primary hover:text-primary/80 transition-colors group"
                  >
                    See full plan comparison
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </a>
                </div>
              </ModalBody>
            </div>
          )}
        </ModalContent>
      </Modal>

      <CheckoutModal
        open={checkoutOpen}
        onOpenChange={(open) => {
          if (!open && loadingPlanId !== null) return;
          setCheckoutOpen(open);
          if (!open) setCheckoutPlan(null);
        }}
        plan={checkoutPlan}
        billing={isAnnual ? 'yearly' : 'monthly'}
        isLoading={loadingPlanId !== null}
        onConfirm={handleConfirmCheckout}
      />

      <Modal
        isOpen={showFreeConfirmModal}
        onOpenChange={(open) => {
          if (!open) setShowFreeConfirmModal(false);
        }}
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold text-foreground">
                  Start Free Explorer Plan?
                </h3>
              </ModalHeader>
              <ModalBody>
                <p className="text-sm text-muted-foreground">
                  You are about to activate the Free Explorer plan and become a
                  service provider on this account. Do you want to continue?
                </p>
              </ModalBody>
              <ModalFooter>
                <CustomButton
                  variant="light"
                  color="default"
                  isDisabled={isLoading}
                  onPress={() => {
                    setShowFreeConfirmModal(false);
                    onClose();
                  }}
                >
                  No
                </CustomButton>
                <CustomButton
                  color="primary"
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  onPress={async () => {
                    await handleBecomeProvider();
                    setShowFreeConfirmModal(false);
                    onClose();
                  }}
                >
                  Yes, continue
                </CustomButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default React.memo(BecomeProviderModal);
